import { colorValues, avatarAccentValues } from '../tokens';
import rgba from '../../functions/rgba';
import { DesignSystemColorMode } from '../types';
import {
    isCheckpointCustomTheme,
    isShyroHealthCustomTheme,
    isWhiteLabelExampleTheme,
} from '@upheal/design-system/theme/legacy/is-whitelabel';
import deepmerge from 'deepmerge';

// TODO figure out how to represent these in tokens even in Figma
const shadow: DesignSystemColorMode['specific']['shadow'] = {
    neutral: {
        strong: rgba('#030712', 0.08),
        medium: rgba('#1F2937', 0.08),
    },
    primary: {
        strong: rgba('#1D4ED8', 0.1),
        medium: rgba('#2563EB', 0.08),
    },
    critical: {
        strong: rgba('#BE123C', 0.1),
        medium: rgba('#E11D48', 0.08),
    },
};

const colors: DesignSystemColorMode = {
    fg: {
        neutral: {
            primary: colorValues.neutral['700'],
            primaryHover: colorValues.neutral['800'],
            primaryActive: colorValues.neutral['900'],
            secondary: colorValues.neutral['600'],
            secondaryHover: colorValues.neutral['700'],
            secondaryActive: colorValues.neutral['800'],
            tertiary: colorValues.neutral['500'],
            tertiaryHover: colorValues.neutral['600'],
            tertiaryActive: colorValues.neutral['700'],
            disabled: colorValues.neutral['400'],
        },
        accent1: {
            primary: colorValues.accent1['600'],
            primaryHover: colorValues.accent1['700'],
            primaryActive: colorValues.accent1['800'],
            secondary: colorValues.accent1['500'],
            secondaryHover: colorValues.accent1['600'],
            secondaryActive: colorValues.accent1['700'],
        },
        accent2: {
            primary: colorValues.accent2['600'],
            primaryHover: colorValues.accent2['700'],
            primaryActive: colorValues.accent2['800'],
            secondary: colorValues.accent2['500'],
            secondaryHover: colorValues.accent2['600'],
            secondaryActive: colorValues.accent2['700'],
        },
        positive: {
            primary: colorValues.success['600'],
            primaryHover: colorValues.success['700'],
            primaryActive: colorValues.success['800'],
            secondary: colorValues.success['500'],
            secondaryHover: colorValues.success['600'],
            secondaryActive: colorValues.success['700'],
        },
        attention: {
            primary: colorValues.attention['700'],
            primaryHover: colorValues.attention['800'],
            primaryActive: colorValues.attention['900'],
            secondary: colorValues.attention['500'],
            secondaryHover: colorValues.attention['600'],
            secondaryActive: colorValues.attention['700'],
        },
        critical: {
            primary: colorValues.critical['600'],
            primaryHover: colorValues.critical['700'],
            primaryActive: colorValues.critical['800'],
            secondary: colorValues.critical['500'],
            secondaryHover: colorValues.critical['600'],
            secondaryActive: colorValues.critical['700'],
        },
        inverse: {
            primary: colorValues.neutral['200'],
            primaryHover: colorValues.neutral['100'],
            primaryActive: colorValues.neutral['50'],
            secondary: colorValues.neutral['300'],
            secondaryHover: colorValues.neutral['200'],
            secondaryActive: colorValues.neutral['100'],
            tertiary: colorValues.neutral['400'],
            tertiaryHover: colorValues.neutral['300'],
            tertiaryActive: colorValues.neutral['200'],
        },
        onAccent: {
            primary: colorValues.alpha.white['900'],
            primaryHover: colorValues.alpha.white['1000'],
            primaryActive: colorValues.alpha.white['1000'],
            secondary: colorValues.alpha.white['800'],
            secondaryHover: colorValues.alpha.white['900'],
            secondaryActive: colorValues.alpha.white['900'],
            tertiary: colorValues.alpha.white['700'],
            tertiaryHover: colorValues.alpha.white['800'],
            tertiaryActive: colorValues.alpha.white['800'],
        },
    },
    bg: {
        neutral: {
            primary: colorValues.neutral['0'],
            primaryHover: colorValues.neutral['50'],
            primaryActive: colorValues.neutral['100'],
            secondary: colorValues.neutral['50'],
            secondaryHover: colorValues.neutral['200'],
            secondaryActive: colorValues.neutral['300'],
            tertiary: colorValues.neutral['100'],
            tertiaryHover: colorValues.neutral['200'],
            tertiaryActive: colorValues.neutral['300'],
            quaternary: colorValues.neutral['200'],
            quaternaryHover: colorValues.neutral['300'],
            quaternaryActive: colorValues.neutral['400'],
            disabled: colorValues.neutral['100'],
        },
        accent1: {
            primary: colorValues.accent1['600'],
            primaryHover: colorValues.accent1['700'],
            primaryActive: colorValues.accent1['800'],
            secondary: colorValues.accent1['200'],
            secondaryHover: colorValues.accent1['300'],
            secondaryActive: colorValues.accent1['400'],
            tertiary: colorValues.accent1['50'],
            tertiaryHover: colorValues.accent1['100'],
            tertiaryActive: colorValues.accent1['200'],
        },
        accent2: {
            primary: colorValues.accent2['600'],
            primaryHover: colorValues.accent2['700'],
            primaryActive: colorValues.accent2['800'],
            secondary: colorValues.accent2['200'],
            secondaryHover: colorValues.accent2['300'],
            secondaryActive: colorValues.accent2['400'],
            tertiary: colorValues.accent2['50'],
            tertiaryHover: colorValues.accent2['100'],
            tertiaryActive: colorValues.accent2['200'],
        },
        positive: {
            primary: colorValues.success['600'],
            primaryHover: colorValues.success['700'],
            primaryActive: colorValues.success['800'],
            secondary: colorValues.success['200'],
            secondaryHover: colorValues.success['300'],
            secondaryActive: colorValues.success['400'],
            tertiary: colorValues.success['50'],
            tertiaryHover: colorValues.success['100'],
            tertiaryActive: colorValues.success['200'],
        },
        attention: {
            primary: colorValues.attention['300'],
            primaryHover: colorValues.attention['400'],
            primaryActive: colorValues.attention['500'],
            secondary: colorValues.attention['200'],
            secondaryHover: colorValues.attention['300'],
            secondaryActive: colorValues.attention['400'],
            tertiary: colorValues.attention['50'],
            tertiaryHover: colorValues.attention['100'],
            tertiaryActive: colorValues.attention['200'],
        },
        critical: {
            primary: colorValues.critical['600'],
            primaryHover: colorValues.critical['700'],
            primaryActive: colorValues.critical['800'],
            secondary: colorValues.critical['300'],
            secondaryHover: colorValues.critical['400'],
            secondaryActive: colorValues.critical['500'],
            tertiary: colorValues.critical['50'],
            tertiaryHover: colorValues.critical['100'],
            tertiaryActive: colorValues.critical['200'],
        },
        inverse: {
            primary: colorValues.neutral['900'],
            primaryHover: colorValues.neutral['800'],
            primaryActive: colorValues.neutral['700'],
            secondary: colorValues.neutral['800'],
            secondaryHover: colorValues.neutral['700'],
            secondaryActive: colorValues.neutral['600'],
            tertiary: colorValues.neutral['700'],
            tertiaryHover: colorValues.neutral['600'],
            tertiaryActive: colorValues.neutral['500'],
        },
    },
    border: {
        sidebarHeader: colorValues.neutral['200'],
        neutral: {
            primary: colorValues.neutral['300'],
            primaryHover: colorValues.neutral['400'],
            primaryActive: colorValues.neutral['500'],
            secondary: colorValues.neutral['200'],
            secondaryHover: colorValues.neutral['300'],
            secondaryActive: colorValues.neutral['400'],
            tertiary: colorValues.neutral['100'],
            tertiaryHover: colorValues.neutral['200'],
            tertiaryActive: colorValues.neutral['300'],
        },
        accent1: {
            primary: colorValues.accent1['400'],
            primaryHover: colorValues.accent1['500'],
            primaryActive: colorValues.accent1['600'],
            secondary: colorValues.accent1['300'],
            secondaryHover: colorValues.accent1['400'],
            secondaryActive: colorValues.accent1['500'],
            tertiary: colorValues.accent1['200'],
            tertiaryHover: colorValues.accent1['300'],
            tertiaryActive: colorValues.accent1['400'],
        },
        accent2: {
            primary: colorValues.accent2['500'],
            primaryHover: colorValues.accent2['600'],
            primaryActive: colorValues.accent2['700'],
            secondary: colorValues.accent2['400'],
            secondaryHover: colorValues.accent2['500'],
            secondaryActive: colorValues.accent2['600'],
            tertiary: colorValues.accent2['200'],
            tertiaryHover: colorValues.accent2['300'],
            tertiaryActive: colorValues.accent2['400'],
        },
        positive: {
            primary: colorValues.success['400'],
            primaryHover: colorValues.success['500'],
            primaryActive: colorValues.success['600'],
            secondary: colorValues.success['300'],
            secondaryHover: colorValues.success['400'],
            secondaryActive: colorValues.success['500'],
            tertiary: colorValues.success['200'],
            tertiaryHover: colorValues.success['300'],
            tertiaryActive: colorValues.success['400'],
        },
        attention: {
            primary: colorValues.attention['400'],
            primaryHover: colorValues.attention['500'],
            primaryActive: colorValues.attention['600'],
            secondary: colorValues.attention['300'],
            secondaryHover: colorValues.attention['400'],
            secondaryActive: colorValues.attention['500'],
            tertiary: colorValues.attention['200'],
            tertiaryHover: colorValues.attention['300'],
            tertiaryActive: colorValues.attention['400'],
        },
        critical: {
            primary: colorValues.critical['400'],
            primaryHover: colorValues.critical['500'],
            primaryActive: colorValues.critical['600'],
            secondary: colorValues.critical['300'],
            secondaryHover: colorValues.critical['400'],
            secondaryActive: colorValues.critical['500'],
            tertiary: colorValues.critical['200'],
            tertiaryHover: colorValues.critical['300'],
            tertiaryActive: colorValues.critical['400'],
        },
        inverse: {
            primary: colorValues.neutral['700'],
            primaryHover: colorValues.neutral['600'],
            primaryActive: colorValues.neutral['500'],
            secondary: colorValues.neutral['800'],
            secondaryHover: colorValues.neutral['700'],
            secondaryActive: colorValues.neutral['600'],
            tertiary: colorValues.neutral['900'],
            tertiaryHover: colorValues.neutral['800'],
            tertiaryActive: colorValues.neutral['700'],
        },
    },
    specific: {
        skeleton: {
            primary: colorValues.alpha.white['1000'],
        },
        charts: {
            primary: colorValues.alpha.white['1000'],
        },
        shadow,
    },
    accents: avatarAccentValues,
    effects: {
        shadow: {
            neutral: {
                xs: `0px 1px 2px 0px ${shadow.neutral.strong}`,
                sm: `0px 2px 4px 0px ${shadow.neutral.strong}, 0px 1px 2px -1px ${shadow.neutral.strong}`,
                md: `0px 4px 8px -1px ${shadow.neutral.strong}, 0px 1px 2px 0px ${shadow.neutral.strong}`,
                lg: `0px 12px 16px -2px ${shadow.neutral.medium}, 0px 4px 8px -2px ${shadow.neutral.medium}, 0px 1px 2px -1px ${shadow.neutral.strong}`,
                xl: `0px 16px 24px -4px ${shadow.neutral.medium}, 0px 12px 20px -4px ${shadow.neutral.medium}, 0px 1px 2px -1px ${shadow.neutral.strong}`,
                '2xl': `0px 24px 40px -4px ${shadow.neutral.medium}, 0px 16px 24px 0px ${shadow.neutral.medium}, 0px 1px 2px -1px ${shadow.neutral.strong}`,
            },
            accent1: {
                xs: `0px 1px 2px 0px ${shadow.primary.strong}`,
                sm: `0px 2px 4px 0px ${shadow.primary.strong}, 0px 1px 2px -1px ${shadow.primary.strong}`,
                md: `0px 4px 8px -1px ${shadow.primary.strong}, 0px 1px 2px 0px ${shadow.primary.strong}`,
                lg: `0px 12px 16px -2px ${shadow.primary.medium}, 0px 4px 8px -2px ${shadow.primary.medium}, 0px 1px 2px -1px ${shadow.primary.strong}`,
                xl: `0px 16px 24px -4px ${shadow.primary.medium}, 0px 12px 20px -4px ${shadow.primary.medium}, 0px 1px 2px -1px ${shadow.primary.strong}`,
                '2xl': `0px 24px 40px -4px ${shadow.primary.medium}, 0px 16px 24px 0px ${shadow.primary.medium}, 0px 1px 2px -1px ${shadow.primary.strong}`,
            },
            critical: {
                xs: `0px 1px 2px 0px ${shadow.critical.strong}`,
                sm: `0px 2px 4px 0px ${shadow.critical.strong}, 0px 1px 2px -1px ${shadow.critical.strong}`,
                md: `0px 4px 8px -1px ${shadow.critical.strong}, 0px 1px 2px 0px ${shadow.critical.strong}`,
                lg: `0px 12px 16px -2px ${shadow.critical.medium}, 0px 4px 8px -2px ${shadow.critical.medium}, 0px 1px 2px -1px ${shadow.critical.strong}`,
                xl: `0px 16px 24px -4px ${shadow.critical.medium}, 0px 12px 20px -4px ${shadow.critical.medium}, 0px 1px 2px -1px ${shadow.critical.strong}`,
                '2xl': `0px 24px 40px -4px ${shadow.critical.medium}, 0px 16px 24px 0px ${shadow.critical.medium}, 0px 1px 2px -1px ${shadow.critical.strong}`,
            },
        },
        focusRing: {
            accent1: colorValues.accent1['400'],
            critical: colorValues.critical['600'],
        },
    },
};

// Also for legacy theme libs/design-system/src/lib/theme/legacy/colors.ts
const exampleWhiteLabelColors = deepmerge(colors, {
    fg: {
        neutral: {
            primary: '#000',
            secondary: '#333',
            tertiary: '#666',
        },
    },
    bg: {
        neutral: {
            primary: '#ffaaaa',
            primaryHover: '#f89999',
            secondary: '#f58888',
            tertiary: '#ee7777',
        },
        accent1: {
            primary: '#00f',
            tertiary: '#00a',
        },
    },
    border: {
        neutral: {
            primary: '#e0e0e0',
            secondary: '#f0f0f0',
        },
    },
});

// Also for legacy theme libs/design-system/src/lib/theme/legacy/colors.ts
// Shyrohealth accent1 color scale derived from primary #76c9ba
// Scale generated to match standard accent1 lightness progression
// Contrast ratios vs white: 600=2.1:1, 700=2.8:1, 800=4.1:1, 900=5.6:1, 950=7.5:1
const shyrohealthAccent1 = {
    '50': '#f0faf8',
    '100': '#e0f5f1',
    '200': '#c7ede6',
    '300': '#a3e0d5',
    '400': '#76c9ba',
    '500': '#4db5a3',
    '600': '#3d9b8a',
    '700': '#308171',
    '800': '#26675a',
    '900': '#1d4d44',
    '950': '#143632',
};
const shyrohealthColors = deepmerge(colors, {
    fg: {
        // Use darker shades for text to meet WCAG AA contrast (4.5:1 min)
        accent1: {
            primary: shyrohealthAccent1['800'],
            primaryHover: shyrohealthAccent1['900'],
            primaryActive: shyrohealthAccent1['950'],
            secondary: shyrohealthAccent1['700'],
            secondaryHover: shyrohealthAccent1['800'],
            secondaryActive: shyrohealthAccent1['900'],
        },
    },
    bg: {
        accent1: {
            // 400 is the original brand color #76c9ba
            primary: shyrohealthAccent1['500'],
            primaryHover: shyrohealthAccent1['600'],
            primaryActive: shyrohealthAccent1['700'],
            secondary: shyrohealthAccent1['100'],
            secondaryHover: shyrohealthAccent1['200'],
            secondaryActive: shyrohealthAccent1['300'],
            tertiary: shyrohealthAccent1['50'],
            tertiaryHover: shyrohealthAccent1['100'],
            tertiaryActive: shyrohealthAccent1['200'],
        },
        inverse: {
            primary: '#f4f4ef', // telehealth call background. TODO: consider defining explicit config in `specific`
        },
    },
    border: {
        accent1: {
            primary: shyrohealthAccent1['400'],
            primaryHover: shyrohealthAccent1['500'],
            primaryActive: shyrohealthAccent1['600'],
            secondary: shyrohealthAccent1['300'],
            secondaryHover: shyrohealthAccent1['400'],
            secondaryActive: shyrohealthAccent1['500'],
            tertiary: shyrohealthAccent1['200'],
            tertiaryHover: shyrohealthAccent1['300'],
            tertiaryActive: shyrohealthAccent1['400'],
        },
    },
});

// Also for legacy theme libs/design-system/src/lib/theme/legacy/colors.ts
const checkpointColors = deepmerge(colors, {
    fg: {
        accent1: {
            primary: colorValues.neutral['0'],
            primaryHover: colorValues.neutral['0'],
            primaryActive: colorValues.neutral['0'],
        },
    },
    bg: {
        accent1: {
            primary: '#028ba6',
            primaryHover: '#028ba6',
            primaryActive: '#028ba6',
            secondary: '#028ba6',
            secondaryHover: '#028ba6',
            secondaryActive: '#028ba6',
            tertiary: '#028ba6',
            tertiaryHover: '#028ba6',
            tertiaryActive: '#028ba6',
        },
    },
    border: {
        accent1: {
            primary: '#028ba6',
            primaryHover: '#028ba6',
            primaryActive: '#028ba6',
            secondary: '#028ba6',
            secondaryHover: '#028ba6',
            secondaryActive: '#028ba6',
            tertiary: '#028ba6',
        },
    },
});

if (isShyroHealthCustomTheme) {
    Object.assign(colors, shyrohealthColors);
}

if (isCheckpointCustomTheme) {
    Object.assign(colors, checkpointColors);
}

if (isWhiteLabelExampleTheme) {
    Object.assign(colors, exampleWhiteLabelColors);
}

export default colors;
